import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Download, MapPin, Printer, RefreshCw, Scale } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errText } from "@/lib/api";
import { fmtTime, longDate, sessionLabel, shortDate, timeRange, typeLabel } from "@/lib/eventMeta";
import { Markdown } from "@/components/Markdown";

const Row = ({ event }) => (
  <div
    data-testid={`clerk-event-${event.event_id}`}
    className="flex flex-col sm:flex-row gap-1 sm:gap-4 items-start border-b border-border py-4 last:border-0"
  >
    <span className="sm:w-28 shrink-0 text-base sm:text-lg font-semibold">
      {event.start_time ? timeRange(event) : sessionLabel(event.session_flag) || "All day"}
    </span>
    <span className="min-w-0">
      <span className="block text-lg font-semibold leading-snug">{event.title}</span>
      <span className="block text-sm text-muted-foreground mt-1">
        {[typeLabel(event.event_type), event.court_name, event.case_number, event.location]
          .filter(Boolean)
          .join(" · ")}
      </span>
      {event.listing_details ? (
        <span className="block text-sm mt-1">
          <strong>Listing: </strong>
          <Markdown
            text={event.listing_details}
            inline
            testId={`clerk-listing-details-${event.event_id}`}
          />
        </span>
      ) : null}
      {event.urgent || event.clash ? (
        <span className="mt-1 inline-block text-xs font-bold uppercase tracking-wider text-fixed">
          {[event.urgent ? "urgent" : null, event.clash ? "clash" : null].filter(Boolean).join(" · ")}
        </span>
      ) : null}
    </span>
  </div>
);

export default function ClerkSheet() {
  const { token } = useParams();
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(token ? `/clerk/public/${token}` : "/clerk/sheet");
      setData(d);
    } catch (e) {
      toast.error(errText(e));
      setData({ error: errText(e) });
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const downloadPdf = async (date) => {
    try {
      const path = token ? `/clerk/public/${token}/sheet.pdf` : "/clerk/sheet.pdf";
      const res = await api.get(path, { params: { date }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `clerk-sheet-${date}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(errText(e));
    }
  };

  if (!data) return <div className="h-64 surface animate-pulse" data-testid="clerk-loading" />;
  if (data.error)
    return (
      <div className="min-h-screen grid place-items-center bg-background px-6">
        <p data-testid="clerk-error" className="text-lg font-semibold text-center">
          {data.error}
        </p>
      </div>
    );

  const body = (
    <div className="space-y-8" data-testid="clerk-sheet-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="label-caps">Clerk sheet · read only</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold mt-2">{data.owner_name}</h1>
          <p className="mt-3 text-sm text-muted-foreground">
            Today {shortDate(data.today.date)} · Tomorrow {shortDate(data.tomorrow.date)}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            data-testid="clerk-refresh-btn"
            onClick={load}
            className="rounded-full h-11"
          >
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
          <Button
            variant="outline"
            data-testid="clerk-print-btn"
            onClick={() => window.print()}
            className="rounded-full h-11 print:hidden"
          >
            <Printer className="h-4 w-4 mr-2" /> Print
          </Button>
          <Button
            data-testid="clerk-pdf-btn"
            onClick={() => downloadPdf(data.tomorrow.date)}
            className="rounded-full h-11"
          >
            <Download className="h-4 w-4 mr-2" /> Tomorrow PDF
          </Button>
        </div>
      </div>

      {[
        ["Tomorrow", data.tomorrow, "tomorrow"],
        ["Today", data.today, "today"],
      ].map(([label, group, key]) => (
        <section key={key} className="surface p-6" data-testid={`clerk-${key}-panel`}>
          <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
            <h2 className="text-base md:text-lg font-heading font-semibold">
              {label} — {longDate(group.date)}
            </h2>
            <div className="flex items-center gap-3">
              <span className="label-caps">{group.events.length} listings</span>
              <button
                data-testid={`clerk-pdf-${key}`}
                onClick={() => downloadPdf(group.date)}
                className="text-sm font-medium hover:underline print:hidden"
              >
                PDF
              </button>
            </div>
          </div>
          {group.events.length ? (
            <div>
              {group.events.map((e) => (
                <Row key={e.event_id} event={e} />
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Nothing listed.</p>
          )}
        </section>
      ))}

      <section className="surface p-6" data-testid="clerk-oos-panel">
        <h2 className="text-base md:text-lg font-heading font-semibold mb-4">
          Out-of-station — next 30 days
        </h2>
        {data.out_of_station.length ? (
          <ul className="space-y-3">
            {data.out_of_station.map((e) => (
              <li
                key={e.event_id}
                data-testid={`clerk-oos-${e.event_id}`}
                className="oos-stripe border border-oos/40 rounded-sm px-4 py-3 flex flex-wrap items-baseline gap-x-4"
              >
                <span className="text-base font-semibold w-40">{shortDate(e.date)}</span>
                <span className="text-base font-semibold inline-flex items-center gap-1.5">
                  <MapPin className="h-4 w-4" /> {e.location || e.title}
                </span>
                <span className="text-sm text-muted-foreground">{e.title}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">No travel commitments listed.</p>
        )}
      </section>
    </div>
  );

  if (!token) return body;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border bg-card">
        <div className="mx-auto max-w-5xl px-6 py-4 flex items-center gap-3">
          <div className="h-9 w-9 grid place-items-center bg-primary text-primary-foreground rounded-sm">
            <Scale className="h-5 w-5" />
          </div>
          <span className="font-heading text-lg font-semibold tracking-tight">
            Chambers of Amir Arsiwala
          </span>
          <span className="ml-auto label-caps">Shared clerk link</span>
        </div>
      </header>
      <main className="mx-auto max-w-5xl px-6 py-10">{body}</main>
    </div>
  );
}
